<script setup>
import { ref, watch, onUnmounted } from 'vue';
import isJSONString from '../../hooks/isJsonString';

const tabs = ref(isJSONString(props.tabs) ? JSON.parse(props.tabs) : []);
const tabsRef = ref();
const isOpen = ref(false);
const eventsSet = ref(false);
const tabListRef = ref();

const props = defineProps({
  tabs: {
    type: String,
    default: '',
    required: true,
  },
});

const openTab = (tab) => {
  tab.setAttribute('aria-selected', true);
  const controls = tab?.getAttribute('aria-controls');
  const toOpen = document.getElementById(controls);
  toOpen?.classList.add('open');
  toOpen?.classList.remove('close');
  setTimeout(() => {
    if (toOpen?.classList.contains('open')) {
      toOpen.style.overflow = 'visible';
    }
  }, 800);
};

const closeTab = (tab) => {
  tab.setAttribute('aria-selected', false);
  const controls = tab?.getAttribute('aria-controls');
  const toClose = document.getElementById(controls);
  if (toClose) {
    toClose.style.overflow = 'hidden';
    toClose.classList.remove('open');
    toClose.classList.add('close');
  }
};

const setActiveTab = (e) => {
  isOpen.value = true;
  const current = document.querySelector('[aria-selected="true"]');

  if (e.target === current) {
    collapseAllTabs();
    return;
  }

  if (current) {
    closeTab(current);
  }

  openTab(e.target);
};

const collapseAllTabs = () => {
  const current = document.querySelector('[aria-selected="true"]');
  closeTab(current);
  isOpen.value = false;
};

const windowClickHandler = (e) => {
  const target = e.target;
  if (tabListRef.value.contains(target)) {
    return;
  }
  if (target.classList.contains('.header__right-tabpanel')) {
    return;
  }
  if (target.closest('.header__right-tabpanel') !== null) {
    return;
  }
  // This needs to be redone, it is on the list of known 'bugs'
  if (target.getAttribute('aria-label') === 'Clear field') {
    return;
  }
  // If all the above fail, the click was outside the components
  collapseAllTabs();
};

const windowEventsHandler = (bool) => {
  if (bool === eventsSet.value) {
    return;
  }
  eventsSet.value = bool;

  if (bool) {
    window.addEventListener('click', windowClickHandler);
    window.addEventListener('scroll', collapseAllTabs);
    window.addEventListener('resize', collapseAllTabs);
  } else {
    window.removeEventListener('click', windowClickHandler);
    window.removeEventListener('scroll', collapseAllTabs);
    window.removeEventListener('resize', collapseAllTabs);
  }
};

watch(isOpen, () => {
  windowEventsHandler(isOpen.value);
});

onUnmounted(() => {
  window.removeEventListener('click', windowClickHandler);
  window.removeEventListener('scroll', collapseAllTabs);
  window.removeEventListener('resize', collapseAllTabs);
});
</script>

<template>
  <!-- Tabs Start-->
  <div ref="tabListRef">
    <ul role="tablist">
      <li v-for="tab in tabs" :key="tab?.id">
        <button
          v-if="tab.icon"
          :id="tab.id"
          ref="tabsRef"
          role="tab"
          aria-selected="false"
          :aria-controls="tab.controls"
          :aria-label="tab?.label"
          class="button button--text button--icon m-t--7"
          @click="setActiveTab"
        >
          <i
            class="icon icon-search icon-color--grey2 icon--xs"
            :class="tab.icon"
            aria-hidden="true"
          ></i>
        </button>
        <button
          v-if="!tab.icon && !tab.URL"
          :id="tab.id"
          ref="tabsRef"
          role="tab"
          aria-selected="false"
          :aria-controls="tab.controls"
          class="button button--text"
          @click="setActiveTab"
        >
          {{ tab?.label }}
        </button>

        <a
          v-if="!tab.icon && tab.URL"
          :id="tab.id"
          ref="tabsRef"
          class="button button--text"
          :href="tab.URL"
          >{{ tab?.label }}</a>
      </li>
    </ul>
  </div>
  <!-- Tabs End-->
</template>
